/**
 * `source_config.extra_data_definition`: per-document fields the indexer reads off each
 * SharePoint item and stores with every chunk (services/indexing/scripts/sharepoint/sharepoint.py,
 * `get_extra_info`). Each row is `{ type, path, display_name, add_to_prompt, add_to_ui, links,
 * select_values? }`; `path` is slash-separated and relative to the item's `fields`, and
 * `select_values` picks keys out of the object(s) the two `list_objects*` types resolve.
 *
 * The indexer never validates the array (an unknown type is silently skipped, a missing type
 * fails the fetch), so this module is the only place that does. Unknown keys and the key order of
 * a stored row are preserved so an untouched definition round-trips unchanged.
 */

import type { SpoInspectedColumn } from "@/api/admin/libraries_indexing"

export const EXTRA_DATA_TYPES = [
  "field",
  "field-lookup",
  "list",
  "list_objects",
  "list_objects_concat",
] as const

export type ExtraDataType = (typeof EXTRA_DATA_TYPES)[number]

const KNOWN_TYPES: ReadonlySet<string> = new Set(EXTRA_DATA_TYPES)

/** Types whose value comes from an object (or a JSON string of one): they need `select_values`. */
const OBJECT_TYPES: ReadonlySet<string> = new Set(["list_objects", "list_objects_concat"])

export function isKnownExtraDataType(type: string): type is ExtraDataType {
  return KNOWN_TYPES.has(type)
}

export function isObjectExtraDataType(type: string): boolean {
  return OBJECT_TYPES.has(type)
}

/** Locale key suffix (`extraData.types.<key>`) for a known type, else null. */
export function extraDataTypeLabelKey(type: string): string | null {
  switch (type) {
    case "field":
      return "field"
    case "field-lookup":
      return "fieldLookup"
    case "list":
      return "list"
    case "list_objects":
      return "listObjects"
    case "list_objects_concat":
      return "listObjectsConcat"
    default:
      return null
  }
}

export const EXTRA_DATA_RENDERS = ["text", "html", "link"] as const

export type ExtraDataRender = (typeof EXTRA_DATA_RENDERS)[number]

/**
 * A row's render mode. Rows stored before this field existed carry only `links`, so that is the
 * fallback -- nothing has to be re-saved for an existing library to keep rendering links.
 *
 * A stored `links: true` beside a default `render` wins, because the write path defers to it too.
 * Resolving that here rather than only on save is what keeps the dropdown honest: it has to show
 * the mode the row would be saved as, or an admin who never touched the row sees "Text", presses
 * Save, and stores a link.
 */
export function renderFromStored(stored: Record<string, unknown>): ExtraDataRender {
  const value = stored.render
  const stated =
    typeof value === "string" && (EXTRA_DATA_RENDERS as readonly string[]).includes(value)
      ? (value as ExtraDataRender)
      : null
  if (stated && stated !== "text") return stated
  return stored.links === true ? "link" : (stated ?? "text")
}

export type ExtraDataRow = {
  /** Client-only identity for React keys and drag-and-drop; never stored. */
  key: string
  /** Kept as a plain string so a stored unknown type still round-trips (and gets flagged). */
  type: string
  path: string
  display_name: string
  add_to_prompt: boolean
  add_to_ui: boolean
  /**
   * How the value is displayed in the metadata dialog. Supersedes `links`, which could only say
   * "anchor or not" and had nothing to offer a SharePoint rich-text column -- those return HTML,
   * so they rendered as raw markup (#3008). The three modes are mutually exclusive, so they are
   * one field rather than a boolean beside an enum, which would allow `links: true` next to
   * `render: "html"`.
   */
  render: ExtraDataRender
  /** Kept in sync with `render` so consumers that still read it keep working. */
  links: boolean
  select_values: string[]
  /** The stored object this row came from: unknown keys and key order survive a save. */
  original: Record<string, unknown>
}

export type ExtraDataRowInput = Omit<ExtraDataRow, "key" | "original">

let nextRowKey = 0

function newRowKey(): string {
  nextRowKey += 1
  return `extra-data-${nextRowKey}`
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {}
}

function asStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : []
}

function str(value: unknown): string {
  return typeof value === "string" ? value : ""
}

/** A fresh, empty `field` row. */
export function newExtraDataRow(input: Partial<ExtraDataRowInput> = {}): ExtraDataRow {
  return {
    key: newRowKey(),
    type: input.type ?? "field",
    path: input.path ?? "",
    display_name: input.display_name ?? "",
    add_to_prompt: input.add_to_prompt ?? false,
    add_to_ui: input.add_to_ui ?? false,
    render: input.render ?? (input.links ? "link" : "text"),
    links: input.links ?? input.render === "link",
    select_values: input.select_values ?? [],
    original: {},
  }
}

/** Rows from the stored array. Anything that is not an object becomes an empty (invalid) row. */
export function extraDataRowsFromConfig(value: unknown): ExtraDataRow[] {
  if (!Array.isArray(value)) return []
  return value.map((entry) => {
    const original = asRecord(entry)
    return {
      key: newRowKey(),
      type: str(original.type),
      path: str(original.path),
      display_name: str(original.display_name),
      add_to_prompt: original.add_to_prompt === true,
      add_to_ui: original.add_to_ui === true,
      render: renderFromStored(original),
      // The stored flag as it actually is, not re-derived from `render`: a hand-edited or
      // old-frontend row can carry `links: true` beside a default `render`, and the write path
      // defers to it rather than silently turning that row into plain text.
      links: original.links === true,
      select_values: asStringList(original.select_values),
      original,
    }
  })
}

/**
 * The stored shape. Starts from the original object so unknown keys keep their place; a flag is
 * written when it is true or when the stored row already had it, so untouched rows do not grow
 * `links: false` on every save. `select_values` is written only for the object types.
 */
export function extraDataRowsToConfig(rows: ExtraDataRow[]): Record<string, unknown>[] {
  return rows.map((row) => {
    const out: Record<string, unknown> = { ...row.original }
    out.type = row.type
    out.path = row.path.trim()
    out.display_name = row.display_name.trim()
    for (const flag of ["add_to_prompt", "add_to_ui"] as const) {
      if (row[flag] || flag in row.original) out[flag] = row[flag]
    }
    // `render` carries the mode; `links` is written alongside it so anything still reading the
    // old flag (the indexer's extra-data passthrough, an older frontend) keeps behaving. Both are
    // written only when they say something, so untouched rows do not grow defaults on every save.
    // `render` is the source of truth, but a caller that only flipped `links` (a spread, or code
    // written before the mode existed) must not silently lose the setting -- so a default `render`
    // defers to it. An explicit non-default `render` always wins.
    let mode: ExtraDataRender = row.render
    if (mode === "text" && row.links) mode = "link"
    // `render` is written only when it says something `links` cannot -- that is, "html". A link is
    // already fully expressed by `links`, and `renderFromStored` reads it back as one, so an
    // untouched legacy row still round-trips byte-identical instead of growing a key on save.
    if (mode === "html" || "render" in row.original) out.render = mode
    const isLink = mode === "link"
    if (isLink || "links" in row.original) out.links = isLink
    if (isObjectExtraDataType(row.type)) {
      out.select_values = row.select_values.map((v) => v.trim()).filter(Boolean)
    } else {
      delete out.select_values
    }
    return out
  })
}

/**
 * A row with a new type. Leaving an object type drops the keys with it: they are meaningless on
 * the other types, the stored shape never carries them there, and keeping them would block save
 * on a rule the admin cannot see (the keys input only shows for the object types).
 */
export function withExtraDataType(row: ExtraDataRow, type: string): ExtraDataRow {
  if (isObjectExtraDataType(type)) return { ...row, type }
  return { ...row, type, select_values: [] }
}

export type ExtraDataRowErrors = Partial<
  Record<"type" | "path" | "display_name" | "select_values", string>
>

/** Minimal translator shape so validation stays testable without i18next. */
type Translate = (key: string, defaultValue: string) => string

/** Per-row errors keyed by row key; an empty map means every row is valid. */
export function validateExtraDataRows(
  rows: ExtraDataRow[],
  t: Translate
): Map<string, ExtraDataRowErrors> {
  const result = new Map<string, ExtraDataRowErrors>()
  for (const row of rows) {
    const errors: ExtraDataRowErrors = {}
    if (!isKnownExtraDataType(row.type)) {
      errors.type = t(
        "adminDashboard.libraries.editor.extraData.errors.typeUnknown",
        "Unknown field type"
      )
    }
    if (!row.path.trim()) {
      errors.path = t(
        "adminDashboard.libraries.editor.extraData.errors.pathRequired",
        "Path is required"
      )
    }
    if (!row.display_name.trim()) {
      errors.display_name = t(
        "adminDashboard.libraries.editor.extraData.errors.displayNameRequired",
        "Display name is required"
      )
    }
    const selected = row.select_values.map((v) => v.trim()).filter(Boolean)
    if (isObjectExtraDataType(row.type) && selected.length === 0) {
      errors.select_values = t(
        "adminDashboard.libraries.editor.extraData.errors.selectValuesRequired",
        "Pick at least one key to read from the object"
      )
    } else if (!isObjectExtraDataType(row.type) && selected.length > 0) {
      errors.select_values = t(
        "adminDashboard.libraries.editor.extraData.errors.selectValuesNotAllowed",
        "Keys only apply to the object types; clear them or change the type"
      )
    }
    if (Object.keys(errors).length > 0) result.set(row.key, errors)
  }
  return result
}

// ----- suggested sets --------------------------------------------------------------------------

export type ExtraDataSuggestion = {
  /** Locale key suffix (`extraData.suggestion.<id>`). */
  id: "standard" | "attachments"
  rows: ExtraDataRowInput[]
}

type SuggestedRowInput = Pick<ExtraDataRowInput, "type" | "path" | "display_name"> &
  Partial<
    Pick<ExtraDataRowInput, "add_to_prompt" | "add_to_ui" | "links" | "render" | "select_values">
  >

/** Suggested rows default to prompt + UI, no link, no keys. */
const row = (input: SuggestedRowInput): ExtraDataRowInput => {
  // A suggested set names `links` for its URL rows; `render` follows from it so the two cannot
  // disagree and the sets stay readable.
  const links = input.links ?? false
  return {
    add_to_prompt: true,
    add_to_ui: true,
    select_values: [],
    ...input,
    links,
    render: input.render ?? (links ? "link" : "text"),
  }
}

/**
 * Document libraries: the indexer merges drive-item metadata into `fields` before extraction
 * (`sharepoint.py`, `document["fields"].update(...)`), so these are flat keys and `createdBy` is
 * already a display name. This is the set every existing library on internal carries.
 */
const DOCUMENT_LIBRARY_STANDARD: ExtraDataRowInput[] = [
  row({ type: "field", path: "webUrl", display_name: "File URL", links: true }),
  row({ type: "field", path: "folderUrl", display_name: "Folder URL", links: true }),
  row({ type: "field", path: "createdBy", display_name: "Created By" }),
  row({ type: "field", path: "lastModifiedDateTime", display_name: "Modified On" }),
  row({ type: "field", path: "lastModifiedBy", display_name: "Modified By" }),
]

/**
 * Generic lists: `_enrich_list_item_fields` (`sharepoint_list.py`) adds the Graph item metadata
 * unflattened, so identities are objects and need the `/user/displayName` path.
 */
const LIST_STANDARD: ExtraDataRowInput[] = [
  row({ type: "field", path: "Title", display_name: "Title" }),
  row({ type: "field", path: "webUrl", display_name: "Item URL", links: true }),
  row({ type: "field", path: "createdBy/user/displayName", display_name: "Created By" }),
  row({ type: "field", path: "lastModifiedDateTime", display_name: "Modified On" }),
  row({ type: "field", path: "lastModifiedBy/user/displayName", display_name: "Modified By" }),
]

/** Attachments are injected under `AttachmentLinks` as `{ name, webUrl, downloadUrl, ... }`. */
const LIST_ATTACHMENTS: ExtraDataRowInput[] = [
  row({
    type: "list_objects",
    path: "AttachmentLinks",
    display_name: "Attachments",
    add_to_prompt: false,
    links: true,
    select_values: ["webUrl"],
  }),
]

/** Avtal lists get the raw Graph item with no enrichment: only native columns are reachable. */
const AVTAL_STANDARD: ExtraDataRowInput[] = [
  row({ type: "field", path: "Title", display_name: "Title" }),
]

/** Pages: the raw Graph `sitePage` is the root, so paths are page properties. */
const PAGES_STANDARD: ExtraDataRowInput[] = [
  row({ type: "field", path: "title", display_name: "Title" }),
  row({ type: "field", path: "webUrl", display_name: "Page URL", links: true }),
  row({ type: "field", path: "lastModifiedDateTime", display_name: "Modified On" }),
  row({ type: "field", path: "lastModifiedBy/user/displayName", display_name: "Modified By" }),
]

export function suggestedExtraDataFor(sourceType: string): ExtraDataSuggestion[] {
  switch (sourceType) {
    case "sharepoint":
    case "spo":
      return [{ id: "standard", rows: DOCUMENT_LIBRARY_STANDARD }]
    case "spo-list":
      return [
        { id: "standard", rows: LIST_STANDARD },
        { id: "attachments", rows: LIST_ATTACHMENTS },
      ]
    case "spo-avtal":
      return [{ id: "standard", rows: AVTAL_STANDARD }]
    case "spo-pages":
      return [{ id: "standard", rows: PAGES_STANDARD }]
    default:
      return []
  }
}

function rowIdentity(type: string, path: string): string {
  return `${type} ${path.trim()}`
}

/** Rows of the suggestion not yet present (same type and path). */
export function missingSuggestedRows(
  rows: ExtraDataRow[],
  suggestion: ExtraDataSuggestion
): ExtraDataRowInput[] {
  const present = new Set(rows.map((r) => rowIdentity(r.type, r.path)))
  return suggestion.rows.filter((r) => !present.has(rowIdentity(r.type, r.path)))
}

/** Append the suggestion's missing rows; existing rows are left untouched. */
export function mergeSuggestedRows(
  rows: ExtraDataRow[],
  suggestion: ExtraDataSuggestion
): ExtraDataRow[] {
  const missing = missingSuggestedRows(rows, suggestion)
  if (missing.length === 0) return rows
  return [...rows, ...missing.map((input) => newExtraDataRow(input))]
}

// ----- columns from the inspect endpoint ------------------------------------------------------

/** The row a column from inspect-sharepoint becomes: the server's type / path, the column's
 * display name, shown to the model and in the UI. */
/**
 * The render mode a column should start with.
 *
 * A SharePoint rich-text column returns HTML, so rendering it as text shows the reader markup
 * instead of the sentence inside it (#3008). Graph reports `textType: "richText"` for exactly
 * those columns -- confirmed against Nolato's CONTENT DESCRIPTION, which reports `richText` while
 * the plain columns beside it report nothing. A `null` text type is not an absent answer to guess
 * at: Graph omits it for single-line columns, where text is the right mode anyway.
 */
function renderForInspectedColumn(column: SpoInspectedColumn): ExtraDataRender {
  const suggested = column.suggested.render
  if (suggested === "text" || suggested === "html" || suggested === "link") return suggested
  // Older backends do not send it; derive the same answer rather than keeping two copies of the
  // rule that could drift apart.
  if (column.suggested.links) return "link"
  return column.text_type === "richText" ? "html" : "text"
}

export function rowFromInspectedColumn(column: SpoInspectedColumn): ExtraDataRowInput {
  const render = renderForInspectedColumn(column)
  return {
    type: column.suggested.type,
    path: column.suggested.path,
    display_name: column.display_name || column.name,
    add_to_prompt: true,
    add_to_ui: true,
    render,
    // Mirrors the mode rather than reading `suggested.links` separately, so a suggested column
    // cannot arrive with the two disagreeing.
    links: render === "link",
    select_values: [],
  }
}

/** True when a row already reads this column (same type and path). */
export function inspectedColumnPresent(rows: ExtraDataRow[], column: SpoInspectedColumn): boolean {
  const identity = rowIdentity(column.suggested.type, column.suggested.path)
  return rows.some((row) => rowIdentity(row.type, row.path) === identity)
}

/** Append rows for the columns not present yet; existing rows are left untouched. */
export function addInspectedColumns(
  rows: ExtraDataRow[],
  columns: SpoInspectedColumn[]
): ExtraDataRow[] {
  const missing = columns.filter((column) => !inspectedColumnPresent(rows, column))
  if (missing.length === 0) return rows
  return [...rows, ...missing.map((column) => newExtraDataRow(rowFromInspectedColumn(column)))]
}
